if (!customElements.get('gf-artwork-picker')) {
  const artworkRequests = new Map();
  customElements.define('gf-artwork-picker', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.gf-more-art__rail');
      this.expand = this.querySelector('.gf-art-expand');
      this.arrows = [...this.querySelectorAll('.gf-art-arrow')];
      this.onClick = event => {
        const toggle = event.target.closest('.gf-art-expand');
        const arrow = event.target.closest('[data-direction]');
        if (toggle) {
          const expanded = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(expanded));
          toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
          this.classList.toggle('is-expanded', expanded);
          this.rail.scrollLeft = 0;
          this.updateControls();
        } else if (arrow) {
          this.rail.scrollBy({left: Number(arrow.dataset.direction) * this.rail.clientWidth * .75, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        } else if (event.target.closest('[data-art-retry]')) {
          this.loadCollection();
        }
      };
      this.onScroll = () => this.updateControls();
      this.addEventListener('click', this.onClick);
      this.rail.addEventListener('scroll', this.onScroll, {passive: true});
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(() => this.updateControls());
        this.observer.observe(this.rail);
      }
      this.syncSelection();
      if (this.dataset.collectionUrl) this.loadCollection();
    }
    syncSelection() {
      const tools = this.closest('product-info')?.querySelector('gf-purchase-tools');
      if (tools?.root) tools.update();
      else {
        let selected = new Map();
        try {
          const names = JSON.parse(this.dataset.referenceOptions || '[]');
          const values = JSON.parse(this.dataset.referenceValues || '[]');
          names.forEach((name, index) => {
            const label = String(name).toLowerCase().trim();
            const normalized = /case|style|finish/.test(label) ? 'case' : /model|device|phone/.test(label) ? 'model' : label;
            if (values[index]) selected.set(normalized, values[index]);
          });
        } catch {}
        this.refresh(selected);
      }
    }
    refresh(selected) {
      const tools = this.closest('product-info')?.querySelector('gf-purchase-tools');
      const keyFor = name => {
        if (tools) return tools.optionKey(name);
        const label = String(name).toLowerCase().trim();
        return /case|style|finish/.test(label) ? 'case' : /model|device|phone/.test(label) ? 'model' : label;
      };
      const ignoreMatch = this.dataset.ignoreMatch === 'true';
      const caseValue = selected.get('case');
      const currentMagSafe = caseValue ? /magsafe/i.test(caseValue) : /magsafe/i.test(this.dataset.currentTitle);
      const limit = Math.min(20, Number(this.dataset.limit) || 20);
      let count = 0;
      this.querySelectorAll('[data-gf-art-link]').forEach(link => {
        let match;
        try {
          const names = JSON.parse(link.dataset.optionNames || '[]').map(keyFor);
          const variants = JSON.parse(link.querySelector('[data-gf-art-variants]')?.textContent || '[]');
          const shared = ignoreMatch ? [] : names.map((key, index) => [key, index]).filter(([key]) => selected.has(key));
          const sameSeparateCase = ignoreMatch || names.includes('case') || /magsafe/i.test(link.dataset.title) === currentMagSafe;
          if (link.dataset.productId !== this.dataset.currentId && (ignoreMatch || !selected.has('model') || names.includes('model')) && sameSeparateCase) {
            match = variants.find(variant => variant.available && shared.every(([key, index]) => String(variant.options[index]).toLowerCase().trim() === String(selected.get(key)).toLowerCase().trim()));
          }
        } catch {}
        link.hidden = !match || count >= limit;
        if (!link.hidden) {
          count++;
          const url = new URL(link.dataset.baseUrl, window.location.origin);
          const localeRoot = window.Shopify?.routes?.root || '/';
          if (localeRoot !== '/' && !url.pathname.startsWith(localeRoot)) url.pathname = localeRoot.replace(/\/$/, '') + url.pathname;
          url.searchParams.set('variant', match.id);
          link.href = url.pathname + url.search;
          link.removeAttribute('aria-disabled');
        } else {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
        }
      });
      this.count = count;
      this.querySelector('.gf-art-empty').hidden = count > 0;
      this.updateControls();
    }
    updateControls() {
      const expanded = this.classList.contains('is-expanded');
      const columns = window.matchMedia?.('(max-width: 749px)').matches ? 3 : 4;
      this.expand.hidden = !expanded && this.count <= columns;
      this.arrows.forEach(arrow => {
        arrow.hidden = expanded || this.rail.scrollWidth <= this.rail.clientWidth + 1;
        arrow.disabled = Number(arrow.dataset.direction) < 0 ? this.rail.scrollLeft <= 1 : this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 1;
      });
    }
    async loadCollection() {
      if (this.loading) return;
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const localeRoot = window.Shopify?.routes?.root || '/';
      if (localeRoot !== '/' && !url.pathname.startsWith(localeRoot)) url.pathname = localeRoot.replace(/\/$/, '') + url.pathname;
      url.searchParams.set('view', 'gf-artwork-source');
      if (this.dataset.sort !== 'collection-default') url.searchParams.set('sort_by', this.dataset.sort || 'best-selling');
      const requestKey = url.pathname + url.search;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('.gf-art-error').hidden = true;
      try {
        if (!artworkRequests.has(requestKey)) {
          const request = fetch(requestKey, {credentials: 'same-origin', headers: {'Accept': 'text/html'}}).then(response => {
            if (!response.ok) throw new Error('Artwork source unavailable');
            return response.text();
          }).then(html => {
            const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gf-artwork-source]');
            if (!source) throw new Error('Artwork source missing');
            return source.innerHTML;
          }).catch(error => { artworkRequests.delete(requestKey); throw error; });
          artworkRequests.set(requestKey, request);
        }
        const html = await artworkRequests.get(requestKey);
        if (!this.isConnected) return;
        this.rail.innerHTML = html;
        this.syncSelection();
      } catch {
        if (this.isConnected) this.querySelector('.gf-art-error').hidden = false;
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.rail?.removeEventListener('scroll', this.onScroll);
      this.observer?.disconnect();
    }
  });
}
